import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {UseFormSetValue} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import useBreakpoint from 'shared/lib/hooks/useBreakpoint';

import Slider from './Slider';

interface ITimeoutSliderProps {
    name: string;
    setValue: UseFormSetValue<any>;
    defaultValue?: number;
    disabled?: boolean;
    onChange?: (_value: number) => void;
}

const MIN_VALUE = 1;
const MAX_VALUE = 60;
const EXTRA_PCT = 5;

const getFillPct = (progressPct: number, seconds: number): number => {
    if (seconds > 1 && seconds <= 20) {
        return Math.min(progressPct + (EXTRA_PCT * (20 - seconds)) / 19, 100);
    }
    return progressPct;
};

const TimeoutSlider: React.FC<ITimeoutSliderProps> = ({
    name,
    defaultValue = 30,
    setValue,
    disabled = false,
    onChange,
}) => {
    const [secondsValue, setSecondsValue] = useState(defaultValue);
    const breakpoint = useBreakpoint();

    const markers = useMemo(() => {
        const lastMarkerPosition = breakpoint === 'mini' ? 92 : 95;
        return [
            {position: 1, value: 1},
            {position: 22, value: 15},
            {position: 47, value: 30},
            {position: 72, value: 45},
            {position: lastMarkerPosition, value: 60},
        ].map(({position, value}) => ({
            key: value,
            position,
            label: `${value} ${t`с`}`,
            overlapped: value <= secondsValue,
        }));
    }, [breakpoint, secondsValue]);

    const progressPct = ((secondsValue - MIN_VALUE) / (MAX_VALUE - MIN_VALUE)) * 100;

    useEffect(() => {
        setValue(name, secondsValue, {shouldDirty: true});
        onChange?.(secondsValue);
    }, [secondsValue, name, setValue, onChange]);

    const onOffset = useCallback((offset: number, width: number) => {
        const rawValue = MIN_VALUE + (offset / width) * (MAX_VALUE - MIN_VALUE);
        setSecondsValue(Math.min(Math.max(Math.round(rawValue), MIN_VALUE), MAX_VALUE));
    }, []);

    return (
        <Slider
            name={name}
            value={secondsValue}
            min={MIN_VALUE}
            max={MAX_VALUE}
            progressPct={progressPct}
            fillPct={getFillPct(progressPct, secondsValue)}
            markers={markers}
            thumbLabel={`${secondsValue} ${t`с`}`}
            disabled={disabled}
            onOffset={onOffset}
        />
    );
};

export default TimeoutSlider;
